"use client";

import { ShieldCheck, Clock, HeartHandshake, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Benefits() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    number: "01",
    title: "FAST APPROVAL",
    description: "Quick decisions with no medical exam required so you can get covered sooner. Our streamlined process ensures you get the protection you need without the long wait.",
    icon: Zap,
    href: "/HomePage",
    offset: "lg:mt-0"
  }, {
    number: "02",
    title: "AFFORDABLE PLANS",
    description: "Flexible coverage options to fit your budget. We offer competitive rates for affordable final expense insurance designed to provide peace of mind without breaking the bank.",
    icon: Clock,
    href: "/HomePage",
    offset: "lg:mt-16"
  }, {
    number: "03",
    title: "FAMILY PROTECTION",
    description: "Help protect loved ones from financial burdens with affordable final expense insurance. Our plans are crafted to ensure your legacy is preserved and your family is secure.",
    icon: ShieldCheck,
    href: "/HomePage",
    offset: "lg:-mt-8"
  }, {
    number: "04",
    title: "NO PRESSURE",
    description: "Friendly help with no medical exam hurdles and no obligation to buy. We believe in empowering you with information so you can make the best choice for your future.",
    icon: HeartHandshake,
    href: "/HomePage",
    offset: "lg:mt-16"
  }];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "InsuranceProduct",
    "name": "Family Legacy Planning",
    "description": "Affordable final expense insurance and burial insurance for seniors with no medical exam required. Protect your family from funeral costs with fast approval.",
    "insuranceType": "Final Expense Insurance",
    "provider": {
      "@type": "Organization",
      "name": "Family Legacy Planning"
    },
    "areaServed": "US"
  };
  return <section data-section-id="5162" id="core-protection-benefits" className="bg-white text-[#061F3F] py-24 md:py-32 lg:py-[140px] overflow-hidden">
      <div className="container mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 lg:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <span className="inline-block text-xs tracking-[0.25em] uppercase font-default mb-8" style={{
            color: "#061F3F"
          }}>
              Why Choose Us
            </span>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-[#061F3F] tracking-tight">
              Protecting your legacy
              <br />
              <span className="italic text-[#061F3F]/80">starts with a burial insurance plan.</span>
            </h2>
          </div>
        </div>

        <div ref={gridRef} className="relative grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10">
          <div aria-hidden="true" className="hidden lg:block absolute top-1/2 left-[12.5%] right-[12.5%] h-px pointer-events-none" style={{
          backgroundColor: "#E5E7EB"
        }} />

          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div data-index={i} key={pillar.number} className={`${pillar.offset} transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: gridVisible ? `${i * 150}ms` : "0ms"
          }}>
                <Card className="group relative h-full bg-white border border-[#E5E7EB] hover:border-[#FFCA2C]/60 rounded-lg p-10 md:p-12 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-lg shadow-md flex flex-col">
                  <CardContent className="p-0 flex flex-col h-full">
                    <span className="font-default text-xs tracking-[0.25em] uppercase text-[#4B5563] mb-6">
                      {pillar.number} / 04
                    </span>

                    <div className="mb-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg border transition-all duration-500 ease-out group-hover:bg-[#061F3F]/10" style={{
                    backgroundColor: "#061F3F",
                    borderColor: "#061F3F"
                  }}>
                        <Icon className="w-8 h-8 transition-all duration-500 ease-out" strokeWidth={1.25} style={{
                      color: "#FFFFFF"
                    }} />
                      </div>
                    </div>

                    <h3 className="font-default text-2xl md:text-[28px] leading-tight text-[#061F3F] mb-5">
                      {pillar.title}
                    </h3>

                    <p className="font-default text-base leading-relaxed text-[#4B5563] mb-10 flex-grow">
                      {pillar.description}
                    </p>

                    <Link to={pillar.href} className="inline-flex items-center gap-2 text-sm font-default uppercase tracking-[0.2em] text-[#061F3F] hover:text-[#FFCA2C] transition-colors duration-300 mt-auto">
                      <span>Learn More</span>
                    </Link>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />
    </section>;
}